import { Calculator, ChartGantt, ChevronDown, Upload } from 'lucide-react';
import { useMemo, useState } from 'react';
import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import type { ScheduleItemView } from '@obraplan/core/application/contracts/schedule-items';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Field } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/states';
import { CronogramaHeader } from '@/features/schedule/CronogramaHeader';
import { EditItemDialog } from '@/features/schedule-items/EditItemDialog';
import { GenerateFromTakeoffDialog } from '@/features/schedule-items/GenerateFromTakeoffDialog';
import { ItemDetailsDialog } from '@/features/schedule-items/ItemDetailsDialog';
import { ScheduleItemsImportWizard } from '@/features/schedule-items/ScheduleItemsImportWizard';
import { ScheduleItemsTable } from '@/features/schedule-items/ScheduleItemsTable';
import { TakeoffDriftBanner } from '@/features/schedule-items/TakeoffDriftBanner';
import { TakeoffRefreshDialog } from '@/features/schedule-items/TakeoffRefreshDialog';
import {
  NO_FILTER,
  disciplinesOf,
  filterScheduleItems,
  formatDecimal,
  formatPeriod,
  groupScheduleItems,
  isStalled,
  towersOf,
  type ScheduleItemFilter,
} from '@/features/schedule-items/schedule-items-model';
import { useTakeoffLines } from '@/features/schedule-items/use-takeoff-lines';
import { ProjectVersionNotice } from '@/features/projects/ProjectVersionNotice';
import { useContainer, usePermission, useSessionState } from '@/features/session/use-session';
import { useCommand } from '@/lib/use-command';
import { useLive } from '@/lib/use-live';

const SELECT_CLASS =
  'h-11 w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-3 text-sm md:h-9';

/**
 * O cronograma por mão de obra (Fase 25; redesenho de 21/10/2026): cada serviço de cada
 * frente, com o prazo, as horas e a barra no tempo, agrupado por torre e pavimento.
 *
 * É uma das três vistas do Cronograma: a de materiais responde "o que precisa chegar e
 * quando"; esta responde "quem faz o quê, em que janela". A janela do item vem do arquivo
 * do GanttPRO, e a linha de base — o prazo de quando o item nasceu — fica por baixo do
 * previsto para mostrar o que escorregou (RN-322).
 *
 * Desde a Fase 31 o item nasce também do **quantitativo** ("Gerar do quantitativo"), e a faixa
 * do topo avisa quando o quantitativo mudou depois disso (RN-419). O quantitativo é lido uma
 * vez, aqui, e só quando alguém precisa dele: a obra com item gerado (a faixa) ou a geração
 * aberta.
 */
export function ScheduleItemsPage() {
  const container = useContainer();
  const today = useMemo(() => container.clock.today('America/Sao_Paulo').toString(), [container]);
  const { session, activeProjectId } = useSessionState();
  const canImport = usePermission(PERMISSION.SCHEDULE_IMPORT);
  const canEdit = usePermission(PERMISSION.SCHEDULE_EDIT);
  // O quantitativo só é lido por quem pode vê-lo: as cinco leituras pedem `takeoff.view`.
  const canReadTakeoff = usePermission(PERMISSION.TAKEOFF_VIEW);
  const canGenerate = canImport && canReadTakeoff;

  const [filter, setFilter] = useState<ScheduleItemFilter>(NO_FILTER);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editing, setEditing] = useState<ScheduleItemView | undefined>(undefined);
  const [viewing, setViewing] = useState<ScheduleItemView | undefined>(undefined);
  const [generating, setGenerating] = useState(false);
  const [reviewing, setReviewing] = useState(false);

  const response = useLive(
    () =>
      session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.listScheduleItems.execute({ projectId: activeProjectId }, session),
    [session?.userId, activeProjectId],
  );
  // A cor de cada disciplina é a cor da barra: vem do esqueleto da obra.
  const outline = useLive(
    () =>
      session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.getProjectOutline.execute({ projectId: activeProjectId }, session),
    [session?.userId, activeProjectId],
  );
  const colors = useMemo(() => {
    const map = new Map<string, string>();
    if (outline !== undefined && outline.ok) {
      for (const discipline of outline.value.availableDisciplines) {
        if (discipline.color !== undefined) map.set(discipline.id, discipline.color);
      }
    }
    return map;
  }, [outline]);

  const all = useMemo<readonly ScheduleItemView[]>(
    () => (response !== undefined && response.ok ? response.value : []),
    [response],
  );
  /** A obra tem item que o quantitativo acompanha — o mesmo critério do servidor (RN-419). */
  const tracksTakeoff = useMemo(
    () =>
      canReadTakeoff &&
      all.some(
        (item) =>
          item.quantitySource === 'QUANTITATIVO' &&
          item.serviceId !== null &&
          item.segment === null,
      ),
    [all, canReadTakeoff],
  );
  const takeoff = useTakeoffLines(tracksTakeoff || generating);
  const towers = useMemo(() => towersOf(all), [all]);
  const disciplines = useMemo(() => disciplinesOf(all, filter.towerId), [all, filter.towerId]);
  const items = useMemo(() => filterScheduleItems(all, filter), [all, filter]);
  const summary = useMemo(() => {
    const groups = groupScheduleItems(items);
    return {
      hours: groups.reduce((total, tower) => total + tower.laborHours, 0),
      start: groups.reduce(
        (min, tower) => (min === '' || tower.start < min ? tower.start : min),
        '',
      ),
      end: groups.reduce((max, tower) => (tower.end > max ? tower.end : max), ''),
      fromTakeoff: items.filter((item) => item.quantitySource === 'QUANTITATIVO').length,
      stalled: items.filter((item) => isStalled(item, today)).length,
    };
  }, [items, today]);

  const [saveItem, saveState] = useCommand(
    (
      item: ScheduleItemView,
      edit: { plannedStart: string; plannedEnd: string; progressPct: number },
    ) =>
      container.updateScheduleItem.execute(
        { projectId: activeProjectId!, itemId: item.id, ...edit },
        session!,
      ),
    {
      successMessage: 'Item atualizado.',
      silent: true,
      onSuccess: () => setEditing(undefined),
    },
  );

  if (activeProjectId === undefined) {
    return (
      <EmptyState
        title="Nenhuma obra selecionada"
        description="Escolha uma obra no seletor do topo para ver o cronograma por mão de obra."
        icon={<ChartGantt className="size-8" />}
      />
    );
  }

  if (response === undefined)
    return <LoadingState label="Carregando o cronograma por mão de obra…" />;
  if (!response.ok)
    return <ErrorState message={response.error.message} code={response.error.code} />;

  // Um botão primário por vista: gerar do quantitativo, ou importar quando não dá para gerar.
  // O resto fica no "Mais".
  const primary = canGenerate ? (
    <Button variant="primary" onClick={() => setGenerating(true)}>
      <Calculator aria-hidden="true" />
      Gerar do quantitativo
    </Button>
  ) : canImport ? (
    <Button variant="primary" onClick={() => setWizardOpen(true)}>
      <Upload aria-hidden="true" />
      Importar cronograma
    </Button>
  ) : undefined;
  const more =
    canImport && canGenerate ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="secondary">
            Mais
            <ChevronDown aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem onSelect={() => setWizardOpen(true)}>
            <Upload aria-hidden="true" />
            Importar cronograma
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ) : undefined;

  const count = items.length;
  const resumo =
    count === 0
      ? 'Nenhum serviço neste filtro.'
      : `${formatPeriod(summary.start, summary.end)} · ${formatDecimal(summary.hours, 0)} HH` +
        (summary.fromTakeoff === count
          ? ' · todos do quantitativo'
          : summary.fromTakeoff > 0
            ? ` · ${String(summary.fromTakeoff)} do quantitativo`
            : '');

  return (
    <div className="flex flex-col gap-5">
      <CronogramaHeader
        atual="/cronograma/itens"
        {...(primary === undefined && more === undefined
          ? {}
          : {
              actions: (
                <>
                  {primary}
                  {more}
                </>
              ),
            })}
      />

      {/* A versão de orçamento não gera cronograma: o aviso diz onde está a obra aprovada. */}
      <ProjectVersionNotice />

      {tracksTakeoff && (
        <TakeoffDriftBanner
          takeoff={takeoff}
          canApply={canImport}
          onOpen={() => setReviewing(true)}
        />
      )}

      {all.length === 0 ? (
        <EmptyState
          title="Cronograma por mão de obra vazio"
          description={
            canImport
              ? `Importe o cronograma exportado do GanttPRO ou o modelo de cronograma por mão de obra${canGenerate ? ' — ou gere a proposta do quantitativo da obra' : ''}. Nada é gravado antes de você conferir.`
              : 'Ninguém importou o cronograma por mão de obra desta obra ainda.'
          }
          icon={<ChartGantt className="size-8" />}
          {...(canImport
            ? { action: { label: 'Importar cronograma', onClick: () => setWizardOpen(true) } }
            : {})}
        />
      ) : (
        <>
          <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
            <p className="text-sm text-(--color-content-muted)" data-testid="resumo-do-cronograma">
              <span className="font-semibold text-(--color-content)">
                {count} {count === 1 ? 'serviço programado' : 'serviços programados'}
              </span>
              {' · '}
              {resumo}
              {summary.stalled > 0 && (
                <>
                  {' · '}
                  <span className="font-medium text-(--color-state-warn)">
                    {summary.stalled} sem avanço dentro do prazo
                  </span>
                </>
              )}
            </p>
            <div className="grid w-full gap-3 sm:w-auto sm:grid-cols-2">
              <Field label="Torre" htmlFor="items-tower">
                <select
                  id="items-tower"
                  className={SELECT_CLASS}
                  value={filter.towerId}
                  onChange={(event) => setFilter({ towerId: event.target.value, disciplineId: '' })}
                >
                  <option value="">Todas as torres</option>
                  {towers.map((tower) => (
                    <option key={tower.id} value={tower.id}>
                      {tower.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Disciplina" htmlFor="items-discipline">
                <select
                  id="items-discipline"
                  className={SELECT_CLASS}
                  value={filter.disciplineId}
                  onChange={(event) => setFilter({ ...filter, disciplineId: event.target.value })}
                >
                  <option value="">Todas as disciplinas</option>
                  {disciplines.map((discipline) => (
                    <option key={discipline.id} value={discipline.id}>
                      {discipline.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </div>

          {items.length === 0 ? (
            <EmptyState
              title="Nada neste filtro"
              description="Nenhum serviço corresponde à torre e à disciplina escolhidas."
              icon={<ChartGantt className="size-8" />}
            />
          ) : (
            <ScheduleItemsTable
              items={items}
              today={today}
              colors={colors}
              onDetails={(item: ScheduleItemView) => setViewing(item)}
              {...(canEdit ? { onEdit: (item: ScheduleItemView) => setEditing(item) } : {})}
            />
          )}

          <p className="max-w-[72ch] text-xs text-(--color-content-muted)">
            Clique num serviço para ver prazo, quantidade, horas e equipe, e para editar.
            {canGenerate &&
              ' As datas seguem o quantitativo: mudou lá, “Gerar do quantitativo” traz para cá.'}
          </p>
        </>
      )}

      <EditItemDialog
        item={editing}
        onOpenChange={(open) => {
          if (!open) setEditing(undefined);
        }}
        onSave={(item, edit) => void saveItem(item, edit)}
        isPending={saveState.isPending}
        errorMessage={saveState.error?.message}
      />

      <ItemDetailsDialog
        item={viewing}
        onOpenChange={(open) => {
          if (!open) setViewing(undefined);
        }}
      />

      {canImport && <ScheduleItemsImportWizard open={wizardOpen} onOpenChange={setWizardOpen} />}

      {canGenerate && (
        <GenerateFromTakeoffDialog
          open={generating}
          onOpenChange={setGenerating}
          takeoff={takeoff}
        />
      )}

      <TakeoffRefreshDialog
        open={reviewing}
        onOpenChange={setReviewing}
        takeoff={takeoff}
        canApply={canImport}
      />
    </div>
  );
}
